<template>
  <div class="dashboard">
    <!-- 欢迎横幅 -->
    <WelcomeBanner :title="greetingText" :subtitle="`欢迎回来，${userName}`">
      <template #right>
        <div v-if="todoCount > 0" class="todo-badge">
          <i class="el-icon-clock"></i>
          <span>{{ todoCount }} 待办</span>
        </div>
        <div v-else class="todo-badge success">
          <i class="el-icon-circle-check"></i>
          <span>全部完成</span>
        </div>
      </template>
    </WelcomeBanner>

    <!-- 数据概览统计 -->
    <el-row :gutter="16" class="stats-row">
      <el-col :xs="12" :sm="6" v-for="stat in statsCards" :key="stat.key">
        <StatCard :value="loading ? '-' : stat.value" :label="stat.label" :icon="stat.icon" :gradient="stat.gradient" />
      </el-col>
    </el-row>

    <!-- 主要内容区 -->
    <el-row :gutter="16" class="content-row">
      <!-- 最近动态 -->
      <el-col :xs="24" :lg="12">
        <div class="panel">
          <div class="panel-header">
            <div class="panel-title">
              <i class="el-icon-time"></i>
              <span>最近动态</span>
            </div>
            <el-button type="text" size="small" @click="refreshActivities" class="refresh-btn">
              <i class="el-icon-refresh"></i>
            </el-button>
          </div>
          <div v-loading="loading" class="activity-list">
            <div v-if="!loading && recentActivities.length === 0" class="empty-state">
              <i class="el-icon-document"></i>
              <p>暂无最近动态</p>
            </div>
            <div v-else v-for="(activity, index) in recentActivities" :key="activity.id" class="activity-item">
              <div class="activity-line" v-if="index > 0"></div>
              <div class="activity-dot" :style="{ backgroundColor: getActivityColor(activity.type) }"></div>
              <div class="activity-content">
                <div class="activity-title">{{ activity.title }}</div>
                <div class="activity-meta">
                  <span class="activity-project">{{ activity.projectName }}</span>
                  <span class="activity-sep">·</span>
                  <span class="activity-time">{{ formatTime(activity.updateTime) }}</span>
                </div>
              </div>
              <el-tag :type="getStatusType(activity.status)" size="mini">{{ getStatusText(activity.status) }}</el-tag>
            </div>
          </div>
          <div class="panel-footer">
            <el-button type="text" size="small" @click="goToTaskList">查看全部 <i class="el-icon-arrow-right"></i></el-button>
          </div>
        </div>
      </el-col>

      <!-- 快捷入口 -->
      <el-col :xs="24" :lg="12">
        <div class="panel">
          <div class="panel-header">
            <div class="panel-title">
              <i class="el-icon-s-grid"></i>
              <span>快捷入口</span>
            </div>
          </div>
          <div class="shortcut-grid">
            <div v-for="shortcut in shortcuts" :key="shortcut.key" class="shortcut-item" @click="handleShortcutClick(shortcut)">
              <div class="shortcut-icon" :style="{ background: shortcut.gradient }">
                <i :class="shortcut.icon"></i>
              </div>
              <span class="shortcut-name">{{ shortcut.name }}</span>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getDataBoardStats } from '../api/dashboard'
import { getUserRecentTasks } from '../api/task'
import StatCard from '@/components/StatCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'Dashboard',
  components: { StatCard, WelcomeBanner },
  data() {
    return {
      loading: true,
      overviewStats: {
        user_projects: 0,
        user_tasks: 0,
        user_sessions: 0,
        user_robots: 0
      },
      recentActivities: [],
      todoCount: 3
    }
  },
  computed: {
    ...mapGetters('user', ['userName', 'avatar']),
    greetingText() {
      const hour = new Date().getHours()
      if (hour < 6) return '凌晨好'
      else if (hour < 9) return '早上好'
      else if (hour < 12) return '上午好'
      else if (hour < 14) return '中午好'
      else if (hour < 18) return '下午好'
      else if (hour < 22) return '晚上好'
      else return '夜深了'
    },
    statsCards() {
      return [
        { key: 'user_projects', label: '项目', value: this.overviewStats.user_projects, icon: 'el-icon-folder-opened', color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' },
        { key: 'user_tasks', label: '任务', value: this.overviewStats.user_tasks, icon: 'el-icon-document', color: '#f97316', gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)' },
        { key: 'user_sessions', label: '会话', value: this.overviewStats.user_sessions, icon: 'el-icon-chat-dot-round', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' },
        { key: 'user_robots', label: '机器人', value: this.overviewStats.user_robots, icon: 'el-icon-user', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)' }
      ]
    },
    shortcuts() {
      return [
        { key: 'templates', name: '流程管理', icon: 'el-icon-s-management', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', path: '/templates' },
        { key: 'sessions', name: '会话管理', icon: 'el-icon-chat-dot-round', gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', path: '/sessions' },
        { key: 'worklogs', name: '工作日志', icon: 'el-icon-document', gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', path: '/work-logs' },
        { key: 'summary', name: '工作汇报', icon: 'el-icon-data-analysis', gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', path: '/summary-list' },
        { key: 'apikeys', name: 'API Key', icon: 'el-icon-key', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', path: '/apikeys' },
        { key: 'hotProjects', name: '热门项目', icon: 'el-icon-star-on', gradient: 'linear-gradient(135deg, #eab308 0%, #facc15 100%)', path: '/projects?filter=hot' },
        { key: 'myTasks', name: '任务管理', icon: 'el-icon-document-checked', gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', path: '/tasks' },
        { key: 'robotList', name: '数字员工', icon: 'el-icon-service', gradient: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)', path: '/robots' }
      ]
    }
  },
  mounted() {
    this.loadDashboardData()
  },
  methods: {
    async loadDashboardData() {
      this.loading = true
      try {
        const stats = await getDataBoardStats()
        if (stats) {
          this.overviewStats = {
            user_projects: stats.user_projects || 0,
            user_tasks: stats.user_tasks || 0,
            user_sessions: stats.user_sessions || 0,
            user_robots: stats.user_robots || 0
          }
          this.todoCount = stats.pending_tasks || 0
        }
        try {
          const recentData = await getUserRecentTasks(10)
          if (recentData && recentData.tasks) {
            this.recentActivities = recentData.tasks.map(task => ({
              id: task.task_id,
              title: task.task_name,
              type: 'task_updated',
              projectName: task.project_id || '未知项目',
              status: task.status === 4 ? 'completed' : 'ongoing',
              updateTime: task.update_time || task.create_time
            }))
          } else {
            this.recentActivities = []
          }
        } catch (e) {
          this.recentActivities = []
        }
      } catch (error) {
        console.error('加载仪表盘数据失败', error)
      } finally {
        this.loading = false
      }
    },
    refreshActivities() { this.loadDashboardData() },
    handleShortcutClick(shortcut) {
      if (shortcut.path) this.$router.push(shortcut.path)
      else if (shortcut.action) shortcut.action()
    },
    goToTaskList() { this.$router.push('/tasks') },
    getActivityIcon(type) {
      return { task_created: 'el-icon-plus', task_updated: 'el-icon-edit', task_completed: 'el-icon-circle-check', comment_added: 'el-icon-chat-dot-round' }[type] || 'el-icon-document'
    },
    getActivityColor(type) {
      return { task_created: '#67C23A', task_updated: '#409EFF', task_completed: '#67C23A', comment_added: '#E6A23C' }[type] || '#909399'
    },
    getStatusType(status) {
      return { pending: 'info', ongoing: 'warning', completed: 'success', cancelled: 'danger' }[status] || 'info'
    },
    getStatusText(status) {
      return { pending: '待开始', ongoing: '进行中', completed: '已完成', cancelled: '已取消' }[status] || '未知'
    },
    formatTime(timeStr) {
      if (!timeStr) return ''
      const date = new Date(timeStr), now = new Date()
      const diff = now.getTime() - date.getTime()
      const minutes = Math.floor(diff / (1000 * 60))
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      if (diff < 1000 * 60) return '刚刚'
      else if (minutes < 60) return `${minutes}分钟前`
      else if (hours < 24) return `${hours}小时前`
      else if (days < 7) return `${days}天前`
      else {
        const year = date.getFullYear(), month = String(date.getMonth() + 1).padStart(2, '0'), day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard {
  padding: 16px;
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
  min-height: calc(100vh - 100px);
}

/* 欢迎横幅 */
.welcome-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.25);
}

.banner-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  display: inline;
}

.banner-sub {
  margin: 0 0 0 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  display: inline;
}

.todo-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 13px;
  color: #fff;
  backdrop-filter: blur(4px);
  
  &.success {
    background: rgba(103, 194, 58, 0.4);
  }
  
  i {
    font-size: 14px;
  }
}

/* 统计卡片 */
.stats-row { margin-bottom: 16px; }

/* 内容行 */
.content-row {
  margin-bottom: 16px;
  display: flex;
  
  &::before, &::after { display: none !important; }
  
  > .el-col {
    display: block;
    flex: 1;
    padding: 0 8px;
    
    &:first-child { padding-left: 0; }
    &:last-child { padding-right: 0; }
    
    > * { height: 100%; }
  }
}

/* 面板 */
.panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 12px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #909399;

  i {
    color: #909399;
    font-size: 14px;
  }
}

.refresh-btn {
  padding: 4px 8px;
  color: #909399;
  
  &:hover { color: #409EFF; }
}

/* 活动列表 */
.activity-list {
  flex: 1;
  padding: 8px 16px;
  max-height: 300px;
  overflow-y: auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #c0c4cc;

  i { font-size: 36px; margin-bottom: 8px; }
  p { margin: 0; font-size: 13px; }
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  position: relative;

  &:last-child { padding-bottom: 0; }
}

.activity-line {
  position: absolute;
  left: 5px;
  top: -10px;
  bottom: 50%;
  width: 1px;
  background: #e4e7ed;
}

.activity-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 12px;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9);
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-title {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.activity-sep {
  color: #dcdfe6;
}

.panel-footer {
  padding: 10px 16px;
  text-align: center;
  border-top: 1px solid #f0f2f5;
}

/* 快捷入口 */
.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 14px;
  flex: 1;
}

.shortcut-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
  min-height: 90px;

  &:hover {
    background: #f5f7fa;
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    
    .shortcut-icon {
      transform: scale(1.1);
    }
  }
}

.shortcut-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  i {
    font-size: 16px;
    color: #fff;
  }
}

.shortcut-name {
  font-size: 12px;
  font-weight: 500;
  color: #909399;
}

/* 响应式 */
@media screen and (max-width: 1200px) {
  .shortcut-grid { grid-template-columns: repeat(4, 1fr); }
}

@media screen and (max-width: 768px) {
  .dashboard { padding: 12px; }
  .welcome-banner { padding: 16px; }
  .banner-title { font-size: 18px; }
  .content-row { flex-direction: column; }
  .content-row > .el-col { padding: 0 0 12px 0 !important; }
  .shortcut-grid { grid-template-columns: repeat(4, 1fr); }
  .shortcut-item { padding: 12px 4px; }
  .shortcut-name { font-size: 11px; }
}
</style>
